/*
 * Client for problem scoping via the builder's Orbit plugin.
 *
 * Deliberately its own file rather than an addition to intakeApi.ts: this lane
 * does not speak the dossier `ConverseResponse` contract at all, and keeping it
 * separate means the two can evolve without colliding.
 *
 * Identity is NOT a parameter. The server reads the company from the session
 * token, so a caller cannot name the company their scoping is filed under.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** One thing to render. The builder's plugin produces richer output than a
 *  field-by-field question, so each kind keeps its own shape. */
export type ScopingEvent =
  | { kind: 'text'; text: string; role?: 'user' | 'assistant' }
  | { kind: 'question'; questions: ScopingQuestion[] }
  /** Catalog matches as data. The tool's raw envelope also carries a `status`
   *  string written for the model, so the server strips it — never render a
   *  tool envelope straight through. */
  | { kind: 'marketplace'; count: number; matches: MarketplaceMatch[]; text?: string }
  | { kind: 'partner'; text: string }
  | { kind: 'prd'; title: string; markdown: string; project_id?: string | null }
  /** The brief is finalized. Carries the build-side project so the app can offer
   *  a signed door to it — the model is not allowed to hand out URLs, because
   *  the build side has no login of its own. */
  | { kind: 'prd_ready'; project_id?: string | null }
  | { kind: 'working'; label: string };

export interface MarketplaceMatch {
  name: string;
  fit?: unknown;
  why?: unknown;
  url?: string | null;
}

export interface ScopingQuestion {
  header?: string;
  question: string;
  multiSelect?: boolean;
  options: Array<{ label: string; description?: string; recommended?: boolean }>;
}

export interface ScopingTurn {
  events: ScopingEvent[];
  problem_id: string | null;
}

export interface ScopingState extends ScopingTurn {
  /** True when nothing has been said yet — the UI shows the opener only then. */
  fresh: boolean;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Thrown when the workspace isn't connected to the builder. Surfaced rather
 *  than swallowed: the alternative is scoping that silently saves nowhere. */
export class ScopingUnavailable extends Error {}

async function parse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({} as any));
  if (res.status === 409) throw new ScopingUnavailable(body?.detail || 'Scoping is not set up for this workspace.');
  if (!res.ok) throw new Error(body?.detail || `Request failed (${res.status})`);
  return body as T;
}

export async function fetchScopingState(): Promise<ScopingState> {
  const res = await fetch(`${API_BASE_URL}/api/orbit/scoping/state`, { headers: authHeaders() });
  return parse<ScopingState>(res);
}

export async function sendScopingTurn(input: string, problemId?: string | null): Promise<ScopingTurn> {
  const res = await fetch(`${API_BASE_URL}/api/orbit/scoping/turn`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input, problem_id: problemId ?? null }),
  });
  return parse<ScopingTurn>(res);
}

/** How the plugin's question card comes back as a message: one line per
 *  question, matching the shape the builder's own planner expects so a card
 *  answered here reads the same as one answered on the marketing site. */
export function formatQuestionAnswers(
  questions: ScopingQuestion[],
  picked: Record<number, string[]>,
): string {
  return questions
    .map((q, i) => {
      const chosen = picked[i] || [];
      if (!chosen.length) return null;
      const header = q.header || q.question;
      return `${header}: ${chosen.join(', ')}`;
    })
    .filter(Boolean)
    .join('\n');
}
